/**
 * My Saved Views (#1144) — feature-owned types.
 *
 * Deliberately separate from the report-owned shapes (`Bill.workspaces`, the
 * `*-report` display contexts): reporting is expected to be removed entirely,
 * so nothing here may reference report code, and sub-record behaviour must
 * never branch on a report context (PRD §2.1).
 */

import type { BillFilters } from '../hooks/useBillFilters';

/** One (bill × workspace) sub-record, as returned by GET /bills. */
export interface BillWorkspaceRecord {
  workspaceId: string;
  workspaceName: string;
  position: string;
  notesCount: number;
  lastNoteAt: string | null;
  /** field_definition_id → stored jsonb value, selected fields only. */
  customFieldValues: Record<string, unknown>;
}

/** One entry in billColumns / workspaceColumns. Array order IS column order. */
export type BillActionKey = 'bookmark' | 'share' | 'votePredictions';

export interface SavedViewColumnEntry {
  colId: string;
  hide?: boolean;
  width?: number;
}

/**
 * Column state stored on a view (PRD §8.3).
 *
 * `billColumns` is applied through AG Grid's own column state. `workspaceColumns`
 * is ours: sub-records render as full-width rows, so those widths become the
 * sub-table's grid template. `null` means "context default columns" — the state
 * of every row saved before #1144.
 */
export interface SavedViewColumns {
  version: 1;
  billColumns: SavedViewColumnEntry[];
  workspaceColumns: SavedViewColumnEntry[];
  subRecordsExpanded: boolean;
  inlineSingleWorkspace: boolean;
  trackedBillsOnly: boolean;
  /** Action buttons shown in the bill-level Actions column, when selected. */
  actions?: BillActionKey[];
}

/** A saved view — org-owned and admin-authored since PRD §15. */
export interface SavedView {
  id: string;
  name: string;
  /** Owning organization (PRD §15). Views are the org's, not the author's. */
  organizationId?: string;
  filters: Partial<BillFilters>;
  searchQuery: string;
  columns: SavedViewColumns | null;
  /** Already intersected server-side with the workspaces the user can reach. */
  workspaceIds: string[];
  isPrimary: boolean;
  createdAt: string;
}

/**
 * Is this view offered inside `workspaceId`?
 *
 * A view is authored at the organization level — on the Legislation page, over
 * whichever workspaces it names — and is then used in those workspaces. So a
 * view built over SANOFI and Angie's List has no business appearing in the
 * 2026 All Bills workspace: its columns describe workspaces that workspace is
 * not, and applying it there would silently narrow to nothing.
 *
 * A view naming NO workspaces is not scoped to any: R8 blocks saving workspace
 * columns without workspaces, so such a view is filters and bill columns only
 * and reads correctly anywhere. Those stay available everywhere.
 *
 * The Legislation page itself never filters — it is the org-level surface where
 * every one of the user's views is listed.
 */
export function isViewAvailableInWorkspace(
  view: SavedView,
  workspaceId: string,
  /** The workspace's owning org, when known — bounds org-wide (no-workspace) views. */
  workspaceOwnerOrgId?: string
): boolean {
  const ids = view.workspaceIds ?? [];
  if (ids.length > 0) return ids.includes(workspaceId);
  /*
    A no-workspace view is org-wide (PRD §15 D-C) — org-wide for ITS org. In
    another org's workspace it does not apply; without a known owner org we
    keep offering it rather than hiding views on missing data.
  */
  return !workspaceOwnerOrgId || !view.organizationId || view.organizationId === workspaceOwnerOrgId;
}

/** Workspace-level colIds. Position/Notes are fixed; custom fields use the cf: prefix. */
export const WORKSPACE_COL_POSITION = 'wsPosition';
export const WORKSPACE_COL_NOTES = 'wsNotes';
export const CUSTOM_FIELD_COL_PREFIX = 'cf:';

export function isCustomFieldColId(colId: string): boolean {
  return colId.startsWith(CUSTOM_FIELD_COL_PREFIX);
}

export function customFieldIdFromColId(colId: string): string | null {
  return isCustomFieldColId(colId) ? colId.slice(CUSTOM_FIELD_COL_PREFIX.length) : null;
}

export function colIdForCustomField(fieldDefinitionId: string): string {
  return `${CUSTOM_FIELD_COL_PREFIX}${fieldDefinitionId}`;
}

/** Empty columns payload for a brand-new view. */
export function emptySavedViewColumns(): SavedViewColumns {
  return {
    version: 1,
    billColumns: [],
    workspaceColumns: [],
    subRecordsExpanded: true,
    inlineSingleWorkspace: true,
    trackedBillsOnly: false,
  };
}
